import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getDropdownsByID,
  getMonthNumber,
  isSouthAfricanID,
} from "../../utils/Statics";
import { StaticTypes } from "../../enums/StaticTypes";
import { Static } from "../../interfaces/Static";
import CustomSelect from "../../components/CustomSelect";
import CustomInput from "../../components/CustomInput";
import { PolicyHolderType } from "../../interfaces/PolicyHolder";
import { useRecoilState } from "recoil";
import { policyHolderState } from "../../state/QuoteState";
import FormHeading from "../../components/FormHeading";
import DateSelector from "../../components/DateSelector";
import {
  extractDetailsFromIdNumber,
  isEmpty,
  isIdNumberValid,
} from "../../utils/Common";
import CustomDivider from "../../components/CustomDivider";
import ErrorText from "../../components/ErrorText";
import Heading from "../../components/Heading";
import { differenceInYears, isValid, parseISO } from "date-fns";

const PolicyHolder = ({
  setIsFormValid,
}: {
  setIsFormValid: (value: boolean) => void;
}) => {
  const { data: statics } = useQuery<Static[]>({ queryKey: ["statics"] });
  const [
    {
      day,
      email,
      employmentStatus,
      gender,
      idNumber,
      idType,
      maritalStatus,
      mobilePhoneNumber,
      month,
      name,
      surname,
      title,
      year,
    },
    setPolicyHolder,
  ] = useRecoilState(policyHolderState);
  const [idNumberNotValid, setIdNumberNotValid] = useState(false);
  const [isPolicyHolderOlderThan18, setIsPolicyHolderOlderThan18] =
    useState<boolean>(false);
  const [isRetiredAndYoungerThan50, setIsRetiredAndYoungerThan50] =
    useState<boolean>(false);
  const [phoneNumberStartsWithZero, setPhoneNumberStartsWithZero] =
    useState<boolean>(true);
  const [phoneNumberLessThan10Digits, setPhoneNumberLessThan10Digits] =
    useState<boolean>(false);
  const [emailNotValid, setEmailNotValid] = useState<boolean | null>(null);

  const { idTypes, titles, genders, maritalStatuses, employmentStatuses } =
    useMemo(() => {
      return {
        idTypes: getDropdownsByID(StaticTypes.IDTYPES, statics || [])?.filter(
          (s) => s?.code !== "3",
          true
        ),
        titles: getDropdownsByID(StaticTypes.TITLES, statics || [], true),
        genders: getDropdownsByID(StaticTypes.GENDERS, statics || []),
        maritalStatuses: getDropdownsByID(
          StaticTypes.MARITAL_STATUSES,
          statics || [],
          true
        ),
        employmentStatuses: getDropdownsByID(
          StaticTypes.EMPLOYMENT_STATUSES,
          statics || [],
          true
        ),
      };
    }, [statics]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isSouthAfricanID(idType)) {
      const payload = extractDetailsFromIdNumber(idNumber);

      setPolicyHolder((prev) => ({ ...prev, ...payload }));
      if (idNumber.length === 13) {
        setIdNumberNotValid(!isIdNumberValid(idNumber));
      } else {
        setIdNumberNotValid(false);
      }
    } else {
      setIdNumberNotValid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idType, idNumber]);

  useEffect(() => {
    const dateString = `${year}-${getMonthNumber(month)}-${day}`;
    if (!isEmpty(day) && !isEmpty(month) && !isEmpty(year)) {
      if (isValid(parseISO(dateString))) {
        const age = differenceInYears(new Date(), new Date(dateString));
        setIsPolicyHolderOlderThan18(age >= 18);
      } else {
        setIsPolicyHolderOlderThan18(false);
      }
    } else {
      setIsPolicyHolderOlderThan18(true);
    }
  }, [day, month, year]);

  useEffect(() => {
    const dateString = `${year}-${getMonthNumber(month)}-${day}`;
    if (employmentStatus === "RETIRED" && isValid(parseISO(dateString))) {
      const age = differenceInYears(new Date(), new Date(dateString));
      setIsRetiredAndYoungerThan50(age < 50);
    } else {
      setIsRetiredAndYoungerThan50(false);
    }
  }, [day, month, year, employmentStatus]);

  useEffect(() => {
    if (
      mobilePhoneNumber.length === 10 &&
      mobilePhoneNumber.slice(0, 1) !== "0"
    ) {
      setPhoneNumberStartsWithZero(false);
    } else {
      setPhoneNumberStartsWithZero(true);
    }

    if (mobilePhoneNumber.length > 1 && mobilePhoneNumber.length < 10) {
      setPhoneNumberLessThan10Digits(true);
    } else {
      setPhoneNumberLessThan10Digits(false);
    }
  }, [mobilePhoneNumber]);

  useEffect(() => {
    if (email.length > 3) {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      setEmailNotValid(!regex.test(email)); // E-Mail Validierung
    } else {
      setEmailNotValid(false);
    }
  }, [email]);

  useEffect(() => {
    setIsFormValid(
      idType !== "" &&
        (isSouthAfricanID(idType) ? idNumber.length === 13 : idNumber !== "") &&
        title !== "" &&
        name !== "" &&
        surname !== "" &&
        day !== "" &&
        month !== "" &&
        year !== "" &&
        gender !== "" &&
        maritalStatus !== "" &&
        employmentStatus !== "" &&
        mobilePhoneNumber !== "" &&
        email !== "" &&
        isPolicyHolderOlderThan18 &&
        !idNumberNotValid &&
        !isRetiredAndYoungerThan50 &&
        phoneNumberStartsWithZero &&
        !phoneNumberLessThan10Digits
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    day,
    email,
    employmentStatus,
    gender,
    idNumber,
    idType,
    maritalStatus,
    mobilePhoneNumber,
    month,
    name,
    surname,
    title,
    year,
    isPolicyHolderOlderThan18,
    idNumberNotValid,
    isRetiredAndYoungerThan50,
    phoneNumberLessThan10Digits,
  ]);

  const handleInputChange = (value: string, field: keyof PolicyHolderType) => {
    setPolicyHolder((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Heading tooltip="The policyholder owns the policy and is responsible for paying the premiums.">
        Policy Holder
      </Heading>
      <FormHeading>Policy holder ID</FormHeading>
      <CustomSelect
        items={idTypes}
        value={idType}
        placeholder="Id Type"
        required
        onSelect={(value: string) => {
          handleInputChange(value, "idType");
        }}
      />
      <CustomInput
        placeholder="ID / Passport Number"
        value={idNumber}
        max={isSouthAfricanID(idType) ? 13 : 40}
        onChange={(value) => {
          handleInputChange(value, "idNumber");
        }}
        required
      />
      {idNumberNotValid && <ErrorText>ID Number not valid!</ErrorText>}

      <CustomDivider />

      <FormHeading>Policy Holder name</FormHeading>
      <CustomSelect
        items={titles}
        value={title}
        placeholder="Title"
        onSelect={(value: string) => {
          handleInputChange(value, "title");
        }}
        required
      />
      <CustomInput
        placeholder="Name"
        value={name}
        onChange={(value) => {
          handleInputChange(value, "name");
        }}
        max={30}
        required
      />
      <CustomInput
        placeholder="Surname"
        value={surname}
        onChange={(value) => {
          handleInputChange(value, "surname");
        }}
        max={30}
        required
      />

      <CustomDivider />

      <FormHeading>Birthdate</FormHeading>
      <DateSelector
        day={day}
        month={month}
        year={year}
        onDaySelect={(value) => {
          handleInputChange(value, "day");
        }}
        onMonthSelect={(value) => {
          handleInputChange(value, "month");
        }}
        onYearSelect={(value) => {
          handleInputChange(value, "year");
        }}
        disabled={isSouthAfricanID(idType)}
        min18Years={true}
        required
      />

      {isPolicyHolderOlderThan18 === false && (
        <ErrorText>
          Policy Holder is younger than 18 Years! The holder has to be older
          than 18 years!
        </ErrorText>
      )}
      {isRetiredAndYoungerThan50 === true && (
        <ErrorText>
          Policy Holder is younger than 50 years and is retired!
        </ErrorText>
      )}

      <CustomDivider />

      <FormHeading>Personal information</FormHeading>
      <CustomSelect
        items={genders}
        value={gender}
        placeholder="Gender"
        onSelect={(value: string) => {
          handleInputChange(value, "gender");
        }}
        required
      />
      <CustomSelect
        items={maritalStatuses}
        value={maritalStatus}
        placeholder="Marital status"
        onSelect={(value: string) => {
          handleInputChange(value, "maritalStatus");
        }}
        required
      />
      <CustomSelect
        items={employmentStatuses}
        value={employmentStatus}
        placeholder="Employment status"
        onSelect={(value: string) => {
          handleInputChange(value, "employmentStatus");
        }}
        required
      />

      <CustomDivider />

      <FormHeading>Contact data</FormHeading>
      <CustomInput
        placeholder="Mobile phone number"
        value={mobilePhoneNumber}
        onChange={(value) => {
          handleInputChange(value, "mobilePhoneNumber");
        }}
        max={10}
        required
      />
      {phoneNumberLessThan10Digits && (
        <ErrorText>The mobile phone number must be 10 numbers long!</ErrorText>
      )}
      {phoneNumberStartsWithZero !== true && (
        <ErrorText>
          The first number of the mobile phone number must be 0!
        </ErrorText>
      )}
      <CustomInput
        placeholder="Email address"
        value={email}
        onChange={(value) => {
          handleInputChange(value, "email");
        }}
        type="email"
        max={40}
        required
      />
      {emailNotValid === true && <ErrorText>Invalid email address!</ErrorText>}
    </>
  );
};

export default PolicyHolder;
